import { useEffect, useMemo, useRef, useState } from 'react';
import type { CommitDTO, TagDTO } from '@clearflow/shared';
import { TAG_COLOR } from './GitGraph.tsx';

interface Props {
  commits: CommitDTO[];
  tags: TagDTO[];
  onSelect: (sha: string) => void;
}

// Discriminant unifiant commits et tags dans une seule liste de résultats/navigation clavier —
// les deux exposent un `sha` (celui du commit tagué pour un tag), donc select()/onKeyDown n'ont
// pas besoin de distinguer les deux cas.
type ResultItem =
  | { kind: 'commit'; sha: string; shortSha: string; title: string; authorName: string }
  | { kind: 'tag'; sha: string; name: string; message: string | null };

function TagIcon() {
  return (
    <svg className="commit-search-tag-icon" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path d="M6 2 L13 2 L13 10 L6 10 L2 6 Z" stroke={TAG_COLOR} strokeWidth="1.3" strokeLinejoin="round" />
      <circle cx="9.5" cy="6" r="1" fill={TAG_COLOR} />
    </svg>
  );
}

export function CommitSearchBar({ commits, tags, onSelect }: Props) {
  const [query, setQuery] = useState('');
  const [author, setAuthor] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const trimmed = query.trim();

  // Commits matching the text query only (before author filter)
  const textMatches = useMemo(() => {
    if (trimmed.length < 2) return [];
    const q = trimmed.toLowerCase();
    return commits.filter((c) =>
      c.sha.startsWith(trimmed) || c.shortSha.startsWith(trimmed) || c.title.toLowerCase().includes(q)
    );
  }, [commits, trimmed]);

  // Tags matching le nom ou le message annoté — pas de notion d'auteur, donc jamais filtrés par
  // le chip auteur ci-dessous (qui ne s'applique qu'aux commits).
  const tagMatches = useMemo(() => {
    if (trimmed.length < 2) return [];
    const q = trimmed.toLowerCase();
    return tags.filter((t) => t.name.toLowerCase().includes(q) || (t.message ?? '').toLowerCase().includes(q));
  }, [tags, trimmed]);

  // Authors present in text matches, with their count
  const authorCounts = useMemo(() => {
    const map = new Map<string, number>();
    for (const c of textMatches) map.set(c.authorName, (map.get(c.authorName) ?? 0) + 1);
    return [...map.entries()].sort((a, b) => b[1] - a[1]);
  }, [textMatches]);

  // Tags d'abord : un nom de tag qui matche est généralement une recherche délibérée et précise,
  // contrairement à une sous-chaîne de message de commit.
  const results = useMemo<ResultItem[]>(() => {
    const commitMatches = author ? textMatches.filter((c) => c.authorName === author) : textMatches;
    const tagItems: ResultItem[] = tagMatches.map((t) => ({ kind: 'tag', sha: t.sha, name: t.name, message: t.message }));
    const commitItems: ResultItem[] = commitMatches.map((c) => ({
      kind: 'commit', sha: c.sha, shortSha: c.shortSha, title: c.title, authorName: c.authorName,
    }));
    return [...tagItems, ...commitItems];
  }, [textMatches, tagMatches, author]);

  const showPopup = trimmed.length >= 2;

  // Reset author when query becomes too short or author disappears from matches
  useEffect(() => {
    if (!showPopup) { setAuthor(''); return; }
    if (author && !authorCounts.some(([a]) => a === author)) setAuthor('');
  }, [showPopup, author, authorCounts]);

  useEffect(() => { setActiveIndex(0); }, [query, author]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setQuery('');
        setAuthor('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  function highlight(text: string, q: string) {
    if (!q) return <>{text}</>;
    const idx = text.toLowerCase().indexOf(q.toLowerCase());
    if (idx === -1) return <>{text}</>;
    return (
      <>
        {text.slice(0, idx)}
        <mark className="commit-search-match">{text.slice(idx, idx + q.length)}</mark>
        {text.slice(idx + q.length)}
      </>
    );
  }

  function select(sha: string) {
    setQuery('');
    setAuthor('');
    onSelect(sha);
    inputRef.current?.blur();
  }

  function onKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Escape') { setQuery(''); setAuthor(''); inputRef.current?.blur(); return; }
    if (!results.length) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setActiveIndex((i) => Math.min(i + 1, results.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActiveIndex((i) => Math.max(i - 1, 0)); }
    else if (e.key === 'Enter') { e.preventDefault(); select(results[activeIndex].sha); }
  }

  return (
    <div ref={containerRef} className="commit-search">
      <div className="commit-search-field">
        <svg className="commit-search-icon" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
          <circle cx="6.5" cy="6.5" r="4.5" stroke="currentColor" strokeWidth="1.5"/>
          <line x1="10" y1="10" x2="14.5" y2="14.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
        </svg>
        <input
          ref={inputRef}
          className="commit-search-input"
          type="text"
          placeholder="Rechercher un commit (SHA, message) ou un tag…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={onKeyDown}
          spellCheck={false}
          style={query ? { paddingRight: 28 } : undefined}
        />
        {query && (
          <button
            type="button"
            className="commit-search-clear"
            aria-label="Effacer la recherche"
            onClick={() => { setQuery(''); setAuthor(''); inputRef.current?.focus(); }}
          >
            <svg viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" width="12" height="12">
              <line x1="3" y1="3" x2="13" y2="13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
              <line x1="13" y1="3" x2="3" y2="13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
          </button>
        )}
      </div>
      {showPopup && (
        <div className="commit-search-popup">
          {authorCounts.length > 1 && (
            <div className="commit-search-authors">
              <button
                className={`commit-search-author-chip${author ? '' : ' active'}`}
                onMouseDown={(e) => { e.preventDefault(); setAuthor(''); }}
              >
                Tous <span className="commit-search-author-count">{textMatches.length}</span>
              </button>
              {authorCounts.map(([name, count]) => (
                <button
                  key={name}
                  className={`commit-search-author-chip${author === name ? ' active' : ''}`}
                  onMouseDown={(e) => { e.preventDefault(); setAuthor(author === name ? '' : name); }}
                >
                  {name} <span className="commit-search-author-count">{count}</span>
                </button>
              ))}
            </div>
          )}
          {results.length > 0 ? (
            <ul className="commit-search-list">
              {results.map((item, i) => (
                <li
                  key={item.kind === 'tag' ? `tag:${item.name}` : `commit:${item.sha}`}
                  className={`commit-search-item${i === activeIndex ? ' active' : ''}`}
                  onMouseEnter={() => setActiveIndex(i)}
                  onMouseDown={(e) => { e.preventDefault(); select(item.sha); }}
                >
                  {item.kind === 'tag' ? (
                    <>
                      <TagIcon />
                      <span className="commit-search-title">{highlight(item.name, trimmed)}</span>
                      {item.message && <span className="commit-search-author">{item.message}</span>}
                    </>
                  ) : (
                    <>
                      <code className="commit-search-sha">{highlight(item.shortSha, trimmed)}</code>
                      <span className="commit-search-title">{highlight(item.title, trimmed)}</span>
                      {authorCounts.length > 1 && !author && (
                        <span className="commit-search-author">{item.authorName}</span>
                      )}
                    </>
                  )}
                </li>
              ))}
            </ul>
          ) : (
            <p className="commit-search-empty">Aucun résultat.</p>
          )}
        </div>
      )}
    </div>
  );
}
